import { useState } from "react";
import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { FieldGroup, Field } from "@/frontend/components/ui/field";
import { InviteShell } from "./InviteShell";
import type { InvitationDetails } from "@/frontend/types/invite";
import { useAcceptInvitationMutation } from "@/frontend/mutations/invite";
import { clearInviteResumeId } from "@/frontend/utils/invite-flow";

export function NameOnlyFlow({
  invitationId,
  invitation,
  currentName,
}: {
  invitationId: string;
  invitation: InvitationDetails;
  currentName: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"form" | "accepting" | "success">("form");

  const acceptMutation = useAcceptInvitationMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      setStep("accepting");
      await acceptMutation.mutateAsync({ invitationId });
      clearInviteResumeId(invitationId);
      setStep("success");
      setTimeout(() => {
        window.location.href = "/dashboard/patients";
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setStep("form");
      setLoading(false);
    }
  };

  if (step === "accepting") {
    return (
      <InviteShell>
        <div className="text-center">
          <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="size-8 text-primary" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">Accepting invitation…</h1>
          <p className="text-sm text-muted-foreground">
            Adding you to{" "}
            <span className="font-medium text-foreground">
              {invitation.organizationName}
            </span>
            .
          </p>
          <div className="mt-6 flex justify-center">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </InviteShell>
    );
  }

  if (step === "success") {
    return (
      <InviteShell>
        <div className="text-center">
          <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
            <CheckCircle2 className="size-8 text-emerald-600" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">You're in!</h1>
          <p className="text-sm text-muted-foreground">
            You've joined{" "}
            <span className="font-medium text-foreground">
              {invitation.organizationName}
            </span>
            . Redirecting you to the dashboard…
          </p>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <FieldGroup>
          <div className="flex flex-col items-center gap-1 text-center mb-2">
            <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 mb-2">
              <Building2 className="size-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">
              Join {invitation.organizationName}
            </h1>
            <p className="text-sm text-muted-foreground text-balance">
              You're joining as{" "}
              <span className="font-medium text-foreground">{currentName}</span>.
            </p>
          </div>
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <Field>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="animate-spin" data-icon="inline-start" />
                  Joining…
                </>
              ) : (
                "Accept invitation"
              )}
            </Button>
          </Field>
        </FieldGroup>
      </form>
    </InviteShell>
  );
}
